import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Divider, Form, FormInstance, Image, Modal, Spin, Upload, UploadProps } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { assetsApi, storageProcessApi } from '../../../../../apis';
import { TakePhotoRequest, UpdateStorageItemPhotos, UpdateTakePhotos } from '../../../../../apis/client-axios';
import ButtonAgree from '../../../../../components/buttonCustom/ButtonAgree';
import ButtonCancel from '../../../../../components/buttonCustom/ButtonCancel';
import FormWrap from '../../../../../components/FormWrap';
import NotificationError from '../../../../../components/notifications/NotificationError';
import NotificationSuccess from '../../../../../components/notifications/NotificationSuccess';
import { QUERY_KEY, TAKE_PHOTO_REQUEST_STATUS } from '../../../../../util/constants';
import { Helper } from '../../../../../util/helper';
import { ValidateLibrary } from '../../../../../validate';
import {
  CloseOutlined,
  LoadingOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons';
import { FileConfig } from '../../../../../constants/config';
import { UploadIcon } from '../../itemDetail/@svg/uploadIcon';
import { DraggerIcon } from '../../itemDetail/@svg/draggerIcon';
import { RemoveIcon } from '../../itemDetail/@svg/removeIcon';
import { TranslateShow, TranslateUnShow } from '../../itemDetail/@svg/translateIcon';

const { Dragger } = Upload;

export interface TakePhotoInterFace {
  activeAddPhoto: { isShow: boolean; takePhotoRequest: TakePhotoRequest };
  setActiveAddPhoto: React.Dispatch<React.SetStateAction<{ isShow: boolean; takePhotoRequest: TakePhotoRequest }>>;
  handleCloseAddPhoto: () => void;
  renderTextStatusPhotoRequest: (value: string) => React.ReactNode;
}
const TakePhotoModal = (props: TakePhotoInterFace) => {
  const { activeAddPhoto, setActiveAddPhoto, handleCloseAddPhoto, renderTextStatusPhotoRequest } = props;
  const intl = useIntl();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<FormInstance>();
  const [isUpload, setIsUpload] = useState(true);
  const [fileList, setFileList] = useState<any>();
  const [fileUploads, setFileUploads] = useState<
    { id: number | null; preview: string; source: File | null }[] | undefined
  >(undefined);
  const [isShowMessageTranslate, setIsShowMessageTranslate] = useState(false);
  const [isLoadingUploadImg, setLoadingUploadImg] = useState(false);
  const [visibleSender, setVisibleSender] = useState(false);
  const [previewSender, setPreviewSender] = useState<{ source: string; index: number } | undefined>(undefined);
  const UploadFile = useMutation({
    mutationFn: (dto: File) => assetsApi.assetControllerUploadFile(dto, 'chat-item').then((res) => res?.data),
    onSuccess: () => {},
    onError: (error) => {},
  });

  const { mutate: translateUserMessage, isPending: isTranslating } = useMutation({
    mutationFn: () =>
      storageProcessApi.storageProcessControllerTranslatePhotoRequest({
        photoRequestId: activeAddPhoto.takePhotoRequest.id,
      }),
    onSuccess: (res) => {
      if (res.data?.length > 0) {
        setActiveAddPhoto({
          ...activeAddPhoto,
          takePhotoRequest: {
            ...activeAddPhoto.takePhotoRequest,
            userMessageTranslated: res.data,
          },
        });
      }
    },
    onError: (error) => {
      setIsShowMessageTranslate(false);
    },
  });

  const UpdateStorageProcessMutation = useMutation({
    mutationFn: (dto: UpdateTakePhotos) => storageProcessApi.storageProcessControllerTakePhotoRequest(dto),
    onSuccess: () => {
      setActiveAddPhoto({
        ...activeAddPhoto,
        isShow: false,
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.ITEM_DETAIL],
        stale: true,
      });
      NotificationSuccess({ content: intl.formatMessage({ id: 'common.updateSuccess' }) });
    },
    onError: (error) => {},
  });

  const UpdatePhotoRequestMutation = useMutation({
    mutationFn: (dto: UpdateStorageItemPhotos) => storageProcessApi.storageProcessControllerUpdatePhotoRequest(dto),
    onSuccess: () => {
      setActiveAddPhoto({
        ...activeAddPhoto,
        isShow: false,
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.ITEM_DETAIL],
        stale: true,
      });
      NotificationSuccess({ content: intl.formatMessage({ id: 'common.updateSuccess' }) });
    },
    onError: (error) => {},
  });

  const handleChangeUploadFile: UploadProps['onChange'] = async ({ fileList: newFileList }) => {
    if (!!newFileList?.length) {
      try {
        if (isUpload) {
          setLoadingUploadImg(true);
          setTimeout(() => {
            const listUrl = newFileList?.map((file: any) => {
              return {
                id: null,
                preview: (window.URL ? URL : webkitURL).createObjectURL(file?.originFileObj),
                source: file?.originFileObj,
              };
            });
            const uploads = fileUploads ? [...fileUploads, ...listUrl] : listUrl;
            setFileUploads(uploads);
            setFileList(newFileList);
            setLoadingUploadImg(false);
            setIsUpload(false);
          }, 2000);
        }
      } catch (error) {
        console.log(error);
        setLoadingUploadImg(false);
      }
    }
  };

  const handleRemoveFile = (file: { preview: string; source: any }) => {
    setFileList(fileList?.filter((item) => item?.uid != file?.source?.uid));
    setFileUploads(fileUploads?.filter((item: any) => item !== file));
  };

  const onFinish = async (value) => {
    if (value?.photoQuantity) {
      if (
        fileUploads?.length < +value?.photoQuantity &&
        activeAddPhoto?.takePhotoRequest?.status !== TAKE_PHOTO_REQUEST_STATUS.REQUESTED
      ) {
        NotificationError({ content: '写真が不足しています' });
        return;
      }
      let assetIds: any = [];
      if (!!fileUploads?.length) {
        const promiseUpload = fileUploads?.map((item) => {
          if (item?.id) return item?.id;
          return UploadFile.mutateAsync(item?.source).then((rs: any) => {
            return rs?.id;
          });
        });
        assetIds = await Promise.all([...promiseUpload]);
      }

      if (activeAddPhoto?.takePhotoRequest?.status === TAKE_PHOTO_REQUEST_STATUS.REQUESTED) {
        UpdateStorageProcessMutation.mutateAsync({
          photoRequestId: activeAddPhoto?.takePhotoRequest?.id,
          quantity: +value?.photoQuantity,
        });
      } else {
        UpdatePhotoRequestMutation.mutateAsync({
          assetIds: assetIds,
          storageItemId: activeAddPhoto?.takePhotoRequest?.storageItemId,
          photoRequestId: activeAddPhoto?.takePhotoRequest?.id,
        });
      }
    }
  };

  const handleTranslateMessage = () => {
    setIsShowMessageTranslate(!isShowMessageTranslate);
    if (!activeAddPhoto?.takePhotoRequest?.userMessageTranslated) {
      translateUserMessage();
    }
  };

  const renderListPhotoUserRequest = useMemo(() => {
    return (activeAddPhoto?.takePhotoRequest as any)?.userPhotos
      ?.sort((a, b) => a?.source?.name?.localeCompare(b?.source?.name, undefined, { numeric: true }))
      .map((item, index) => {
        return (
          <div className="item-detail_add-photo_body_list_item">
            <Image
              key={index}
              onClick={() => {
                setVisibleSender(true);
                setPreviewSender({
                  source: item?.source,
                  index: +index + 1,
                });
              }}
              width={84}
              height={84}
              className="rounded"
              src={Helper.getSourceFile(item?.source)}
            />
            {/* <button
            onClick={() => {
              saveAs(Helper.getSourceFile(item?.source), item.name);
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M14.0002 9.33331C13.8234 9.33331 13.6538 9.40355 13.5288 9.52857C13.4037 9.6536 13.3335 9.82317 13.3335 9.99998V12.6666C13.3335 12.8435 13.2633 13.013 13.1382 13.1381C13.0132 13.2631 12.8436 13.3333 12.6668 13.3333H3.3335C3.15668 13.3333 2.98712 13.2631 2.86209 13.1381C2.73707 13.013 2.66683 12.8435 2.66683 12.6666V9.99998C2.66683 9.82317 2.59659 9.6536 2.47157 9.52857C2.34654 9.40355 2.17697 9.33331 2.00016 9.33331C1.82335 9.33331 1.65378 9.40355 1.52876 9.52857C1.40373 9.6536 1.3335 9.82317 1.3335 9.99998V12.6666C1.3335 13.1971 1.54421 13.7058 1.91928 14.0809C2.29436 14.4559 2.80306 14.6666 3.3335 14.6666H12.6668C13.1973 14.6666 13.706 14.4559 14.081 14.0809C14.4561 13.7058 14.6668 13.1971 14.6668 12.6666V9.99998C14.6668 9.82317 14.5966 9.6536 14.4716 9.52857C14.3465 9.40355 14.177 9.33331 14.0002 9.33331ZM7.52683 10.4733C7.59023 10.534 7.66499 10.5816 7.74683 10.6133C7.82663 10.6486 7.91292 10.6668 8.00016 10.6668C8.08741 10.6668 8.1737 10.6486 8.2535 10.6133C8.33533 10.5816 8.41009 10.534 8.4735 10.4733L11.1402 7.80665C11.2657 7.68111 11.3362 7.51085 11.3362 7.33331C11.3362 7.15578 11.2657 6.98552 11.1402 6.85998C11.0146 6.73444 10.8444 6.66392 10.6668 6.66392C10.4893 6.66392 10.319 6.73444 10.1935 6.85998L8.66683 8.39331V1.99998C8.66683 1.82317 8.59659 1.6536 8.47157 1.52858C8.34654 1.40355 8.17697 1.33331 8.00016 1.33331C7.82335 1.33331 7.65378 1.40355 7.52876 1.52858C7.40373 1.6536 7.3335 1.82317 7.3335 1.99998V8.39331L5.80683 6.85998C5.74467 6.79782 5.67088 6.74851 5.58966 6.71487C5.50845 6.68123 5.4214 6.66392 5.3335 6.66392C5.24559 6.66392 5.15854 6.68123 5.07733 6.71487C4.99611 6.74851 4.92232 6.79782 4.86016 6.85998C4.798 6.92214 4.7487 6.99593 4.71506 7.07715C4.68142 7.15836 4.6641 7.24541 4.6641 7.33331C4.6641 7.42122 4.68142 7.50826 4.71506 7.58948C4.7487 7.67069 4.798 7.74449 4.86016 7.80665L7.52683 10.4733Z"
                fill="#514B4D"
              />
            </svg>
          </button> */}
          </div>
        );
      });
  }, [visibleSender, activeAddPhoto]);

  useEffect(() => {
    form.resetFields();
    form.setFieldValue('photoQuantity', activeAddPhoto?.takePhotoRequest?.photoQuantity);

    if (!!(activeAddPhoto?.takePhotoRequest as any)?.photos?.length) {
      const photos = (activeAddPhoto?.takePhotoRequest as any)?.photos?.map((photo) => {
        return {
          id: photo?.id,
          preview: Helper.getSourceFile(photo?.preview),
          source: null,
        };
      });

      setFileUploads(photos);
    }
  }, []);

  const renderButtonTranslate = () => {
    return (
      <div className="item-detail_add-photo_body_note_translate_button">
        {isTranslating ? (
          <Spin
            style={{ position: 'unset', display: 'flex', height: 24, width: 24 }}
            spinning={isTranslating}
            indicator={<LoadingOutlined style={{ fontSize: 21 }} spin />}
          />
        ) : (
          <button type="button" onClick={handleTranslateMessage}>
            {isShowMessageTranslate ? <TranslateShow /> : <TranslateUnShow />}
          </button>
        )}
      </div>
    );
  };

  return (
    <Modal
      open={activeAddPhoto.isShow}
      onCancel={handleCloseAddPhoto}
      footer={null}
      centered
      className="modal-request-img"
      closeIcon={false}
    >
      <FormWrap form={form} onFinish={onFinish}>
        <div className="item-detail_add-photo_body">
          <div className="item-detail_add-photo_body_top">
            <h6>{intl.formatMessage({ id: 'item.upload.modal.title' })}</h6>
            <span className={`item-detail_add-photo_body_top_${activeAddPhoto?.takePhotoRequest?.status}`}>
              {renderTextStatusPhotoRequest(activeAddPhoto?.takePhotoRequest?.status)}
            </span>
          </div>
          <div className="item-detail_add-photo_body_quantity">
            {/* <label htmlFor="数量">数量</label> */}
            <Form.Item
              label={
                <span className="color-sub-title font-size-16">
                  {intl.formatMessage({ id: 'item.upload.modal.quantity.label' })}
                </span>
              }
              className="m-b-0"
              name="photoQuantity"
              required={activeAddPhoto?.takePhotoRequest?.photoQuantity <= 0}
              rules={ValidateLibrary(intl).photoQuantity}
            >
              <input
                className={activeAddPhoto?.takePhotoRequest?.photoQuantity <= 0 ? 'can-change' : ''}
                type="text"
                id="数量"
                readOnly={activeAddPhoto?.takePhotoRequest?.photoQuantity > 0}
              />
            </Form.Item>
          </div>
          <div className="item-detail_add-photo_body_note">
            <label htmlFor="依頼の説明">{intl.formatMessage({ id: 'item.upload.modal.note.label' })}</label>
            <textarea name="" id="依頼の説明" readOnly value={activeAddPhoto?.takePhotoRequest?.userMessage}></textarea>
            {renderButtonTranslate()}
            <div className="item-detail_add-photo_body_note_translate_message">
              {isShowMessageTranslate ? activeAddPhoto?.takePhotoRequest?.userMessageTranslated : ''}
            </div>
          </div>
          <div className="item-detail_add-photo_body_wrap">
            <label>添付画像</label>
            <div className="item-detail_add-photo_body_list user-upload">
              <Image.PreviewGroup
                preview={
                  visibleSender
                    ? {
                        rootClassName: `chat-tab-image-preview__container  `,
                        onChange: (current, prev) => {
                          setPreviewSender({
                            source: (activeAddPhoto?.takePhotoRequest as any)?.userPhotos[current]?.source,
                            index: current,
                          });
                        },
                        src: Helper.getSourceFile(previewSender?.source),
                        toolbarRender: (
                          _,
                          {
                            transform: { scale },
                            actions: { onRotateLeft, onRotateRight, onZoomOut, onZoomIn, onClose },
                          },
                        ) => (
                          <div className="ant-image-preview-operations">
                            <div className="ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut">
                              <RotateLeftOutlined onClick={onRotateLeft} />
                            </div>
                            <div className="ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut">
                              <RotateRightOutlined onClick={onRotateRight} />
                            </div>
                            <div
                              className={`ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut ${scale === 1 && 'ant-image-preview-operations-operation-disabled'}`}
                            >
                              <ZoomOutOutlined disabled={true} onClick={onZoomOut} />
                            </div>
                            <div className="ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut">
                              <ZoomInOutlined disabled={scale === 50} onClick={onZoomIn} />
                            </div>
                            <div className="ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut">
                              <Divider type="vertical" className="bg-white" />
                            </div>
                            <div className="ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut">
                              <CloseOutlined
                                disabled={scale === 50}
                                onClick={() => {
                                  onClose();
                                }}
                              />
                            </div>
                          </div>
                        ),
                      }
                    : false
                }
              >
                {renderListPhotoUserRequest}
              </Image.PreviewGroup>
            </div>
          </div>
          <div className="item-detail_add-photo_body_wrap">
            {activeAddPhoto?.takePhotoRequest?.status !== TAKE_PHOTO_REQUEST_STATUS.REQUESTED && (
              <h6 className="item-detail_add-photo_body_wrap_title">
                <p>{intl.formatMessage({ id: 'item.upload.upload-2' })}</p>
                <Upload
                  className="cursor-pointer"
                  multiple={true}
                  showUploadList={false}
                  onChange={handleChangeUploadFile}
                  beforeUpload={() => {
                    setIsUpload(true);
                    setFileList([]);
                  }}
                  fileList={fileList}
                  accept={FileConfig.imageAccept}
                  disabled={activeAddPhoto?.takePhotoRequest?.status === TAKE_PHOTO_REQUEST_STATUS.UPLOAD_BY_STAFF}
                >
                  <button type="button" className={`${activeAddPhoto?.takePhotoRequest?.status?.toLocaleLowerCase()}`}>
                    <UploadIcon />
                    <p>{intl.formatMessage({ id: 'item.upload.btn' })}</p>
                  </button>
                </Upload>
              </h6>
            )}
            {activeAddPhoto?.takePhotoRequest?.status === TAKE_PHOTO_REQUEST_STATUS.TAKE_BY_OPERATION && (
              <Spin spinning={isLoadingUploadImg}>
                <Form.Item name="file-upload" className="m-b-0 m-t-16">
                  <Dragger
                    name="file"
                    multiple={true}
                    showUploadList={false}
                    onChange={handleChangeUploadFile}
                    beforeUpload={() => {
                      setIsUpload(true);
                      setFileList([]);
                    }}
                    fileList={fileList}
                    accept={FileConfig.imageAccept}
                    height={85}
                    className="upload-modal"
                  >
                    <div>
                      <DraggerIcon />
                      <p>{intl.formatMessage({ id: 'item.upload.drag' })}</p>
                    </div>
                  </Dragger>
                </Form.Item>
              </Spin>
            )}
            <div className="item-detail_add-photo_body_list">
              {!!fileUploads?.length &&
                fileUploads
                  ?.sort((a, b) => a?.source?.name?.localeCompare(b?.source?.name, undefined, { numeric: true }))
                  .map((file) => {
                    return (
                      <div className="item-detail_add-photo_body_list_item">
                        <img src={file.preview} alt="" />
                        {activeAddPhoto?.takePhotoRequest?.status !== TAKE_PHOTO_REQUEST_STATUS.UPLOAD_BY_STAFF && (
                          <button
                            onClick={(e) => {
                              e?.preventDefault();
                              e?.stopPropagation();
                              handleRemoveFile(file);
                            }}
                          >
                            <RemoveIcon />
                          </button>
                        )}
                      </div>
                    );
                  })}
            </div>
          </div>
          <div className="m-t-60"></div>
          {activeAddPhoto?.takePhotoRequest?.status !== TAKE_PHOTO_REQUEST_STATUS.UPLOAD_BY_STAFF && (
            <div className="item-detail_add-photo_body_button">
              <ButtonCancel
                value={intl.formatMessage({ id: 'chat-item.uploads.cancel' })}
                onClick={handleCloseAddPhoto}
              />
              <Spin
                spinning={
                  UpdatePhotoRequestMutation.isPending || UpdateStorageProcessMutation.isPending || UploadFile.isPending
                }
              >
                <ButtonAgree
                  value={intl.formatMessage({ id: `chat-item.uploads.${activeAddPhoto?.takePhotoRequest?.status}` })}
                />
              </Spin>
            </div>
          )}
        </div>
      </FormWrap>
    </Modal>
  );
};

export default TakePhotoModal;
